import { ThemeEvents } from '@theme/events';
import { getStoreAvailability, hasBranchStock, parseVariantIdList } from '@theme/store-availability';

/**
 * @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent
 */

/**
 * Enforces the variant metafield `custom.externes_lager_ignorieren` on the product page.
 *
 * Variants with that flag may only be sold from branch stock (Aarburg/Wallisellen):
 * external stock and "Continue selling when out of stock" are ignored. Shopify itself
 * doesn't know about that rule, so the server-rendered page shows such a variant as
 * normally available; this component checks the live per-location stock (via the
 * shared "@theme/store-availability" cache, also used by the "Lieferzeit" and
 * "Lagerbestand Filialen" blocks) and, if neither branch has stock, switches the page
 * to the sold-out state:
 *  - add to cart button (in the buy buttons and the sticky bar) disabled, "Ausverkauft"
 *  - accelerated checkout buttons hidden
 *  - inventory status ("Lieferbar") switched to "Nicht vorrätig"
 * The "Lieferzeit" block handles the same rule itself (product-delivery-time.js).
 *
 * It only ever downgrades the server-rendered state: every variant switch re-renders
 * the normal state from the server, after which this component re-applies the check.
 * If the live check fails, the variant is treated as sold out (fail closed), since it
 * must not be sold without confirmed branch stock.
 */
class BranchStockGate extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    // variant:update bubbles; listening on the document avoids depending on which
    // ancestor reliably receives it in this shop's markup (see product-sku.js /
    // product-delivery-time.js). Events from outside this section (e.g. quick add
    // modals) are filtered out in the handler.
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Warm the shared cache for all restricted variants, so the check after a variant
    // switch resolves (almost) immediately instead of leaving a window in which the
    // freshly rendered, enabled add to cart button could be clicked.
    for (const variantId of this.#restrictedVariantIds) {
      this.#fetchLocations(variantId).catch(() => {});
    }

    this.#check(this.dataset.variantId);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * Read on every use rather than cached: when a combined listing switches product,
   * the whole <main> is morphed and this element's attributes change in place.
   */
  get #restrictedVariantIds() {
    return parseVariantIdList(this.dataset.restrictedVariants);
  }

  get #section() {
    return this.closest('.shopify-section') ?? document;
  }

  /**
   * @param {Event} event - Actually a VariantUpdateEvent (extends Event, not CustomEvent).
   */
  #handleVariantUpdate = (event) => {
    const section = this.#section;
    if (section !== document && !(event.target instanceof Node && section.contains(event.target))) return;

    const variant = /** @type {VariantUpdateEvent} */ (event).detail?.resource;
    this.dataset.variantId = variant?.id ? String(variant.id) : '';
    this.#check(this.dataset.variantId);
  };

  /**
   * @param {string} variantId
   */
  #fetchLocations(variantId) {
    return getStoreAvailability(variantId, {
      shopDomain: this.dataset.shopDomain ?? '',
      storefrontToken: this.dataset.storefrontToken ?? '',
    });
  }

  /**
   * @param {string} [variantId]
   */
  async #check(variantId) {
    if (!variantId || !this.#restrictedVariantIds.has(variantId)) return;

    let sellable = false;
    try {
      sellable = hasBranchStock(await this.#fetchLocations(variantId));
    } catch {
      sellable = false; // fail closed, see class comment
    }

    // Always yield at least once, so the other components' variant:update handlers
    // (which morph in the new server-rendered state) have run before we apply ours.
    await Promise.resolve();
    if (variantId !== this.dataset.variantId) return; // a newer variant switch beat us here

    if (!sellable) this.#applySoldOut();
  }

  #applySoldOut() {
    const section = this.#section;
    const soldOutText = this.dataset.soldOutText ?? '';
    const outOfStockText = this.dataset.outOfStockText ?? '';

    // Add to cart buttons: buy buttons block(s) and the sticky bar.
    const buttons = section.querySelectorAll(
      'product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = true;
      button.querySelector('.add-to-cart-icon')?.remove();
      // Buy buttons nest the label as content > span > span, the sticky bar as
      // content > span (followed by the quantity display).
      const label = button.querySelector('.add-to-cart-text__content')?.firstElementChild;
      const labelText = label?.firstElementChild ?? label;
      if (labelText) labelText.textContent = soldOutText;
    }

    for (const sticky of section.querySelectorAll('sticky-add-to-cart')) {
      if (!(sticky instanceof HTMLElement)) continue;
      sticky.dataset.variantAvailable = 'false';
      const quantityDisplay = sticky.querySelector('[ref="quantityDisplay"]');
      if (quantityDisplay instanceof HTMLElement) quantityDisplay.style.display = 'none';
    }

    for (const accelerated of section.querySelectorAll('product-form-component [ref="acceleratedCheckoutButtonContainer"]')) {
      accelerated.setAttribute('hidden', 'true');
    }

    // Inventory status ("Lieferbar" / "Niedriger Lagerbestand" -> "Nicht vorrätig").
    for (const inventory of section.querySelectorAll('product-inventory')) {
      const icon = inventory.querySelector('.product-inventory__icon');
      if (icon) {
        icon.classList.remove('product-inventory__icon-in_stock', 'product-inventory__icon-low');
        icon.classList.add('product-inventory__icon-out_of_stock');
      }
      const text = inventory.querySelector('.product-inventory__text');
      if (text) text.textContent = outOfStockText;
    }
  }
}

if (!customElements.get('branch-stock-gate')) {
  customElements.define('branch-stock-gate', BranchStockGate);
}
